import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ChartProps {
  data: {
    id: string;
    name: string;
    visitors: number;
    conversions: number;
    conversionRate: number;
    engagementRate: number;
    avgTimeOnPage: string;
    bounceRate: number;
  }[];
}

const ABTestCharts: React.FC<ChartProps> = ({ data }) => {
  // Calculate the maximum values for scaling
  const maxConversionRate = Math.max(...data.map(v => v.conversionRate));
  const maxEngagementRate = Math.max(...data.map(v => v.engagementRate));
  const minBounceRate = Math.min(...data.map(v => v.bounceRate));
  const maxBounceRate = Math.max(...data.map(v => v.bounceRate));

  // Helper to get color based on performance
  const getBarColor = (value: number, metric: 'conversion' | 'engagement' | 'bounce') => {
    if (metric === 'conversion' || metric === 'engagement') {
      // Higher is better for conversion and engagement
      const percentage = value / (metric === 'conversion' ? maxConversionRate : maxEngagementRate);
      if (percentage > 0.9) return 'bg-green-500';
      if (percentage > 0.7) return 'bg-green-400';
      if (percentage > 0.5) return 'bg-blue-500';
      if (percentage > 0.3) return 'bg-blue-400';
      return 'bg-gray-400';
    } else {
      // Lower is better for bounce rate
      const range = maxBounceRate - minBounceRate;
      const percentage = range > 0 ? (value - minBounceRate) / range : 0.5;
      if (percentage < 0.2) return 'bg-green-500';
      if (percentage < 0.4) return 'bg-green-400';
      if (percentage < 0.6) return 'bg-blue-500';
      if (percentage < 0.8) return 'bg-blue-400';
      return 'bg-gray-400';
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Conversion Rate Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Conversion Rate</CardTitle>
          <CardDescription>Percentage of visitors who converted</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {data.map(variant => (
              <div key={`conv-${variant.id}`} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{variant.name}</span>
                  <span className="font-bold">{variant.conversionRate}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-4">
                  <div 
                    className={`h-4 rounded-full ${getBarColor(variant.conversionRate, 'conversion')}`} 
                    style={{ width: `${(variant.conversionRate / maxConversionRate) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Engagement Rate Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Engagement Rate</CardTitle>
          <CardDescription>User interaction with page content</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {data.map(variant => (
              <div key={`eng-${variant.id}`} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{variant.name}</span>
                  <span className="font-bold">{variant.engagementRate}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-4">
                  <div 
                    className={`h-4 rounded-full ${getBarColor(variant.engagementRate, 'engagement')}`} 
                    style={{ width: `${(variant.engagementRate / maxEngagementRate) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Bounce Rate Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Bounce Rate</CardTitle>
          <CardDescription>Visitors who leave without interaction</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {data.map(variant => (
              <div key={`bounce-${variant.id}`} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{variant.name}</span>
                  <span className="font-bold">{variant.bounceRate}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-4">
                  <div 
                    className={`h-4 rounded-full ${getBarColor(variant.bounceRate, 'bounce')}`} 
                    style={{ width: `${(variant.bounceRate / maxBounceRate) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Conversion Funnel */}
      <Card>
        <CardHeader>
          <CardTitle>Conversion Funnel</CardTitle>
          <CardDescription>Visitors to conversions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {data.map(variant => {
              const conversionWidth = 100;
              const visitorWidth = conversionWidth * (variant.visitors / variant.conversions);
              
              return (
                <div key={`funnel-${variant.id}`} className="space-y-2">
                  <div className="font-medium">{variant.name}</div>
                  <div className="relative h-16">
                    {/* Visitors bar */}
                    <div 
                      className="absolute top-0 left-0 h-8 bg-blue-200 rounded-l-md flex items-center pl-2"
                      style={{ width: `${Math.min(visitorWidth, 100)}%` }}
                    >
                      <span className="text-xs font-medium text-blue-800">
                        {variant.visitors} visitors
                      </span>
                    </div>
                    
                    {/* Conversions bar */}
                    <div 
                      className="absolute bottom-0 left-0 h-8 bg-green-500 rounded-l-md flex items-center pl-2"
                      style={{ width: `${conversionWidth}%` }}
                    >
                      <span className="text-xs font-medium text-white">
                        {variant.conversions} conversions
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ABTestCharts; 